import { css } from "@emotion/css";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as RefreshIcon } from "../../assets/images/refresh_icon.svg";
import { ReactComponent as RefreshIconDark } from "../../assets/images/refresh_icon_dark.svg";
import Bootcamp from "../../components/Bootcamp";
import { HeaderCustomIconButton } from "../../components/CustomIconButton";
import LavaroPopup from "../../components/LavaroPopup";
import ContentLayout from "../../components/layout-components/ContentLayout";
import TimesheetPopup from "../../components/TimesheetPopup";
import AppContext from "../../config/AppContext";
import {
  getAiPredictionCount,
  listUserIntegrations,
} from "../../services/IntegrationService";
import { getProjectsByLoggedUser } from "../../services/ProjectService";
import { exportTimesheet } from "../../services/WorklogService";
import CommonUtil from "../../util/CommonUtils";
import { TimesheetContext, sidebarContext } from "../../util/context";
import ProjectFilter from "../dashboard/ProjectFilter";
import TimesheetWeekPicker from "../dashboard/TimesheetWeekPicker";
import AITimesheetPredictions from "../worklog/AITimesheetPredictions";
import EditWorkLog from "../worklog/EditWorkLog";
import LogWork from "../worklog/LogWork";
import Worklog from "../worklog/Worklog";
import ActivityList from "./ActivityList";
import Day from "./Day";
import TimesheetProjectReport from "./TimesheetProjectReport";
import TimesheetToolBar from "./TimesheetToolBar";
import Week from "./Week";
import WeekViewMemberFilter from "./WeekViewMemberFilter";

export default function Timesheet() {
  const { mode } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterDone, setIsFilterDone] = useState(false);
  const { enableAi, enableAiPredictions } = useContext(sidebarContext);
  const [reload, setReload] = useState(false);
  const [logSubmited, setLogSubmited] = useState(false);
  const [biotimeTransactions, setBiotimeTransactions] = useState([]);
  const [textSummary, setTextSummary] = useState(false);
  const [generateSummary, setGenerateSummary] = useState(false);
  const [eventsToLog, setEventsToLog] = useState([]);
  const [eventByDrag, setEventByDrag] = useState(undefined);
  const [zoomLevel, setZoomLevel] = useState(-1);
  const zoomLevelRef = useRef(zoomLevel);
  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const [timesheetType, setTimesheetType] = useState("day");
  const [timesheetView, setTimesheetView] = useState("calendar");
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [resetWeekCalendar, setResetWeekCalendar] = useState(false);
  const timeRef = useRef();
  const [selectedMemberName, setSelectedMemberName] = useState("Ralvie AI");
  const [predictioncount, setPredictionCount] = useState(-1);
  const [memberFilter, setMemberFilter] = useState(
    CommonUtil.getLoggedUser()?.id
  );
  const [projectFilter, setProjectFilter] = useState("All projects");
  const [selectedProjects, setSelectedProjects] = useState(new Set());

  const [projectReportOpen, setProjectReportOpen] = useState(false);
  const [myIntegrations, setMyIntegrations] = useState([]);
  const { t } = useTranslation();
  const [filter, setFilter] = useState({
    logged: true,
    unlogged: true,
    hidden: false,
    title: "",
    applicationName: "",
    selectedDate: dayjs(),
    userId: undefined,
  });
  const [firstInnerWidth, setFirstInnerWidth] = useState(75);
  const [isResizing, setIsResizing] = useState(false);
  const { subscriptionDetails } = useContext(sidebarContext);
  const containerRef = useRef(null);
  const resizeRequestRef = useRef(null);
  const currentX = useRef(0);
  const startX = useRef(0);
  const [timesheetToEdit, setTimesheetToEdit] = useState(undefined);
  const [timesheetEvents, setTimesheetEvents] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view");
  const [isAILoading, setIsAILoading] = useState(false);
  const [loggedEventsIds, setLoggedEventsIds] = useState([]);
  const [isHasEvent, setIsHasEvent] = useState(false);
  const [subEventSelected, SetSubEventSelected] = useState([]);
  const [showBootcamp, setShowBootcamp] = useState(false);
  const [hasTimesheetFeature, setHasTimesheetFeature] = useState(false);
  const [
    currentEditingTimesheetProjectId,
    setCurrentEditingTimesheetProjectId,
  ] = useState("");
  const [unSelectedEvents, setUnSelectedEvents] = useState([]);
  const [showSelectionAnimation, setShowSelectionAnimation] = useState(false);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (view === "day") {
      setTimesheetType("day");
    } else if (view === "week") {
      setTimesheetType("week");
    }
  }, []);

  useEffect(() => {
    navigate("/timesheet?view=" + timesheetType);
  }, [timesheetType]);

  const updateWidth = useCallback(() => {
    const scaleFactor = 0.5;
    const deltaX = (currentX.current - startX.current) * scaleFactor;
    const newWidth =
      (deltaX / containerRef.current.offsetWidth) * 100 + firstInnerWidth;
    setFirstInnerWidth(Math.min(Math.max(newWidth, 50), 75));
    resizeRequestRef.current = requestAnimationFrame(updateWidth);
  }, [firstInnerWidth]);

  const handleMouseDown = useCallback(
    (e) => {
      setIsResizing(true);
      startX.current = e.clientX;
      const handleMouseMove = (moveEvent) => {
        currentX.current = moveEvent.clientX;
      };
      const handleMouseUp = () => {
        setIsResizing(false);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        if (resizeRequestRef.current) {
          cancelAnimationFrame(resizeRequestRef.current);
          resizeRequestRef.current = null;
        }
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      resizeRequestRef.current = requestAnimationFrame(updateWidth);
    },
    [updateWidth]
  );

useEffect(() => {
  if (subscriptionDetails && typeof subscriptionDetails === "object") {
    const feature = subscriptionDetails?.features?.find(
      (feature) => feature?.feature?.code === "TIMESHEET"
    );
    if (feature) {
      const featureValue = feature?.value;
      const hasAccess = featureValue.split(',').some(option => 
        option.trim().toLowerCase() === 'all' || 
        option.trim().toLowerCase() === 'ai time sheet'
      );
      setHasTimesheetFeature(hasAccess);
    } else {
      setHasTimesheetFeature(false);
    }
  }
}, [subscriptionDetails]);

  useEffect(() => {
    return () => {
      if (resizeRequestRef.current) {
        cancelAnimationFrame(resizeRequestRef.current);
      }
    };
  }, []);

  useEffect(() => {
    listUserIntegrations().then((res) => {
      if (res.status === 200 && res.data.data) {
        setMyIntegrations(res.data.data.integrations);
      }
    });
    getAiPredictionCount().then((res) => {
      if (res.status === 200 && res.data.code === "RCI0000") {
        setPredictionCount(res.data.data);
        // if (Number(res.data.data.count) === 10 && !localStorage.getItem('bootcampDismissed')) {
        //   setShowBootcamp(true);
        // }
      }
    });
  }, []);

  const [isLogged, setIsLogged] = useState(false);
  useEffect(() => {
    if (isLogged) {
      getAiPredictionCount().then((res) => {
        if (res.status === 200 && res.data.code === "RCI0000") {
          if (Number(res.data.data.count) === 10) {
            setShowBootcamp(true);
          }
        }
      });
      setIsLogged(false);
    }
  }, [isLogged]);

  const handleFilter = (e) => {
    let name = e.target.name;
    if (name === "applicationName") {
      setFilter({
        ...filter,
        [name]: e.target.value,
      });
      return;
    }
    if (name === "title") {
      setFilter({
        ...filter,
        [name]: e.target.value,
      });
      return;
    }
    setFilter({
      ...filter,
      [name]: e.target.checked,
    });
  };
  const handleDateChange = (date) => {
    setTimesheetEvents([]);
    setFilter({ ...filter, selectedDate: date });
    setEventsToLog([]);
    SetSubEventSelected([]);
  };
  const handleZoom = (type) => {
    switch (type) {
      case "in": {
        if (zoomLevel === 30 || zoomLevel === -1) {
          setZoomLevel(20);

          zoomLevelRef.current = 20;
        } else if (zoomLevel === 20) {
          setZoomLevel(15);

          zoomLevelRef.current = 15;
        } else if (zoomLevel === 15) {
          setZoomLevel(10);

          zoomLevelRef.current = 10;
        } else if (zoomLevel === 10) {
          setZoomLevel(5);
          zoomLevelRef.current = 5;
        }
        break;
      }
      case "out":
        if (zoomLevel === 5) {
          setZoomLevel(10);
          zoomLevelRef.current = 10;
        } else if (zoomLevel === 10) {
          setZoomLevel(15);
          zoomLevelRef.current = 15;
        } else if (zoomLevel === 15) {
          setZoomLevel(20);
          zoomLevelRef.current = 20;
        } else if (zoomLevel === 20) {
          setZoomLevel(30 || zoomLevel === -1);
          zoomLevelRef.current = 30;
        }
        break;
      default:
        setZoomLevel(zoomLevel);
    }
  };
  const handleMemberFilterChange = (newMemberFilter) => {
    setMemberFilter(newMemberFilter);
  };

  const handleExport = (start, end, userId) => {
    start = start + "T00:00:00Z";
    end = end + "T23:59:00Z";
    var utc = require("dayjs/plugin/utc");
    var timezone = require("dayjs/plugin/timezone");
    dayjs.extend(utc);
    dayjs.extend(timezone);
    let dateTz = dayjs.tz(start, CommonUtil.getTimeZoneId());
    let startDate = dateTz.startOf("day");
    dateTz = dayjs.tz(end, CommonUtil.getTimeZoneId());
    let endDate = dateTz.endOf("day");
    setIsLoading(true);
    exportTimesheet(
      startDate.utc().format(),
      endDate.utc().format(),
      userId
    ).then((r) => {
      setIsLoading(false);
    });
  };
  useEffect(() => {
    const fetchProject = async () => {
      try {
        let updatedProjects = [];
        const res = await getProjectsByLoggedUser();
        updatedProjects = res?.data?.data?.map((project) => ({
          id: project?.project?.id,
          name: project?.project?.name,
          assignedMembers: project?.project?.assignedMembers || [],
        }));

        updatedProjects?.sort((a, b) => a.name.localeCompare(b.name));
        setProjects(updatedProjects);
        setSelectedProjects(
          new Set([...updatedProjects.map((project) => project.id)])
        );
      } catch (error) {
        console.error("Error fetching project:", error);
      }
    };

    if (
      timesheetType === "week" &&
      CommonUtil.isOwnerOrAdminOrManagerOrHRManager()
    ) {
      fetchProject();
    }
  }, [timesheetType]);

  const actions = (
    <Box display="flex">
      {/* {timesheetType === "day" && enableAi && (
        <GenerateSummary
          height={isXlScreen ? "50px" : "40px"}
          onClick={() => {
            setGenerateSummary(!generateSummary);
            setEventsToLog([]);
          }}
        />
      )} */}

      {/* <ExportDialog
        title="Export timesheet"
        handleExport={handleExport}
      ></ExportDialog> */}

      {/* {timesheetType === "day" && (
        <TimesheetDatePicker
          id="btn-day-clndr"
          onChange={(date) => {
            handleDateChange(date);
          }}
          selectedDate={filter.selectedDate}
        />
      )} */}
      {timesheetType === "week" && (
        <>
          <TimesheetWeekPicker
            id="btn-week-clndr"
            ref={timeRef}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            reset={resetWeekCalendar}
            setResetWeekCalendar={setResetWeekCalendar}
            selectedDate={dayjs()}
          />
          <>
            {CommonUtil.isOwnerOrAdminOrManagerOrHRManager() ? (
              <Box>
                <ProjectFilter
                  id="btn-project-filter-timesheet"
                  projectFilter={projectFilter}
                  setProjectFilter={(val) => {
                    setProjectFilter(val);
                    setMemberFilter(CommonUtil.getLoggedUser()?.id);
                  }}
                  setSelectedProjectName={() =>
                    console.log("setSelectedProjectName")
                  }
                  timesheetType={timesheetType}
                  projects={projects}
                  setProjects={setProjects}
                  setSelectedProjects={setSelectedProjects}
                  selectedProjects={selectedProjects}
                />
              </Box>
            ) : (
              <></>
            )}
            {CommonUtil.isOwnerOrAdminOrTeamleadOrManagerOrHRManager() && (
              <Box pr={2}>
                <WeekViewMemberFilter
                  id="btn-memberfilter-timesheet"
                  memberFilter={memberFilter}
                  setMemberFilter={handleMemberFilterChange}
                  setSelectedMemberName={setSelectedMemberName}
                  departmentFilter={undefined}
                  timesheetType={timesheetType}
                  projectFilter={projectFilter}
                  projects={projects}
                />
              </Box>
            )}
          </>
        </>
      )}
      <Box
        ml={0.5}
        mr={2.5}
        className={css`
          border: 1px solid var(--right-left-arrow-border-color);
          border-radius: 5px;
          padding: 7px;
          opacity: 1;
          align-items: center;
          height: ${isXlScreen ? "50px" : "40px"};
          display: flex;
        `}
      >
        <Box
          // className={css`
          //   background: ${timesheetType === "day"
          //     ? "var(--background-color-8)"
          //     : "var(--background-color-9)"};
          //   opacity: 1;
          //   min-height: 100%;
          //   display: flex;
          //   align-items: center;
          //   padding: 0px 24px;
          //   cursor: pointer;
          //   border-radius: 4px;
          //   :hover {
          //     background: var(--background-color-7);
          //     &.text-label {
          //       color: ${timesheetType === "day"
          //         ? "var(--text-color-4) !important"
          //         : "var(--text-color-8) !important"};
          //     }
          //   }
          // `}
          sx={{
            background:
              timesheetType === "day"
                ? "var(--background-color-8)"
                : "var(--background-color-9)",
            opacity: 1,
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0px 24px",
            cursor: "pointer",
            borderRadius: "4px",
            "& .text-label": {
              color:
                timesheetType === "day"
                  ? "var(--text-color-4) !important"
                  : "var(--text-secondary-50) !important",
            },
            "&:hover": {
              background: "var(--background-color-7)",
              "& .text-label": {
                color:
                  timesheetType === "day"
                    ? "var(--text-color-4) !important"
                    : "var(--text-color-8) !important",
              },
            },
          }}
          onClick={() => setTimesheetType("day")}
          id="btn-day-timesheet"
        >
          <Typography variant="activity_view" className={`text-label`}>
            {t("RAL0330")}
          </Typography>
        </Box>
        <Box
          sx={{
            background:
              timesheetType === "week"
                ? "var(--background-color-8)"
                : "var(--background-color-9)",
            opacity: 1,
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0px 24px",
            borderRadius: "4px",
            cursor: "pointer",
            "& .text-label": {
              color:
                timesheetType === "week"
                  ? "var(--text-color-4) !important"
                  : "var(--text-secondary-50) !important",
            },
            "&:hover": {
              background: "var(--background-color-7)",
              "& .text-label": {
                color:
                  timesheetType === "week"
                    ? "var(--text-color-4) !important"
                    : "var(--text-color-8) !important",
              },
            },
          }}
          onClick={() => {
            setTimesheetType("week");
            setGenerateSummary(false);
            setTextSummary(undefined);
          }}
          id="btn-week-timesheet"
        >
          <Typography variant="activity_view" className={`text-label`}>
            {t("RAL0562")}
          </Typography>
        </Box>
        {/* <Box
          className={css`
            background: ${timesheetType === "month" ? "#E8E6F1" : ""};
            opacity: 1;
            min-height: 100%;
            display: flex;
            align-items: center;
            padding: 0px 24px;
            cursor: pointer;
            :hover {
              background: #e8e6f1;
            }
          `}
          onClick={() => {
            setTimesheetType("day");
            setGenerateSummary(false);
            setTextSummary(undefined);
          }}
          id="btn-month-timesheet"
        >
          <Typography variant="activity_view">Month</Typography>
        </Box> */}
      </Box>
      <Box
        item
        sx={{
          "& .refresh-svg": {
            transition: "color 0.3s ease",
            color: mode === "dark" ? "var(--color-33-50)" : "#242424cc",
          },
          "&:hover": {
            "& .hover-text, & .refresh-svg": {
              color: mode === "dark" ? "#f8f8f8cc" : "#242424",
            },
          },
        }}
      >
        <HeaderCustomIconButton
          id="btn-refresh-timesheet"
          title={t("RAL0169")}
          size={isXlScreen ? "large" : "md"}
          variant="square"
          // mode={mode}
          lightMode={
            <RefreshIcon
              height={isXlScreen ? "18px" : "14px"}
              className="refresh-svg"
            />
          }
          darkMode={
            <RefreshIconDark
              height={isXlScreen ? "18px" : "14px"}
              className="refresh-svg"
            />
          }
          onClick={() => {
            setFilter({
              logged: true,
              unlogged: true,
              hidden: false,
              title: "",
              applicationName: "",
              selectedDate: dayjs(),
            });
            if (timesheetType === "day") {
              setMemberFilter("Self");
            } else if (timesheetType === "week") {
              setMemberFilter(CommonUtil.getLoggedUser()?.id);
              setProjectFilter("All projects");
            }
            setStartDate(dayjs().startOf("week").toDate());
            setEndDate(dayjs().endOf("week").toDate());
            setResetWeekCalendar(true);
            setEventsToLog([]);
            SetSubEventSelected([]);
          }}
        />
      </Box>
    </Box>
  );
  return (
    <>
      <TimesheetContext.Provider
        value={{
          eventByDrag,
          setEventByDrag,
          reload,
          setReload,
          eventsToLog,
          setEventsToLog,
          textSummary,
          setTextSummary,
          biotimeTransactions,
          setBiotimeTransactions,
          timesheetToEdit,
          setTimesheetToEdit,
          timesheetEvents,
          setTimesheetEvents,
          logSubmited,
          setLogSubmited,
          isAILoading,
          setIsAILoading,
          loggedEventsIds,
          setLoggedEventsIds,
          SetSubEventSelected,
          subEventSelected,
          isHasEvent,
          setIsHasEvent,
          currentEditingTimesheetProjectId,
          setCurrentEditingTimesheetProjectId,
          unSelectedEvents,
          setUnSelectedEvents,
        }}
      >
        <ContentLayout
          title={t("RAL0328")}
          actions={actions}
          isLoading={timesheetType === "week" ? isLoading : false}
        >
          {timesheetType === "week" && (
            <Week
              myIntegrations={myIntegrations}
              isLoading={isLoading}
              setIsLoading={(state) => setIsLoading(state)}
              startDate={startDate}
              endDate={endDate}
              generateSummary={generateSummary}
              setGenerateSummary={(state) => setGenerateSummary(state)}
              userId={memberFilter}
              projectFilter={projectFilter}
            />
          )}
          {timesheetType === "day" && (
            <div
              ref={containerRef}
              className={css`
                background-color: var(--main-body-background-color);
                // border-radius: 5px;
                height: 100%;
                width: 100%;
                display: flex;
                cursor: ${isResizing ? "ew-resize" : "default"};
              `}
            >
              <div
                className={css`
                  height: 100%;
                  width: ${firstInnerWidth}%;
                  overflow: scroll;
                  background: var(--background-color-3);
                `}
              >
                <div className="timeSheetBar">
                  <TimesheetToolBar
                    firstInnerWidth={firstInnerWidth}
                    filter={filter}
                    setFilter={setFilter}
                    handleFilter={handleFilter}
                    handleDateChange={handleDateChange}
                    zoomLevel={zoomLevelRef.current}
                    isXlScreen={isXlScreen}
                    timesheetView={timesheetView}
                    timesheetType={timesheetType}
                    setTimesheetView={(view) => setTimesheetView(view)}
                    handleZoom={(type) => handleZoom(type)}
                    isFilterDone={isFilterDone}
                    isHasEvent={isHasEvent}
                  />
                  {timesheetView === "calendar" ? (
                    <Day
                      setIsLoading={(state) => setIsLoading(state)}
                      setIsFilterDone={setIsFilterDone}
                      // zoomLevel={zoomLevel}
                      filter={filter}
                      handleFilter={handleFilter}
                      zoomLevel={zoomLevelRef.current}
                      isXlScreen={isXlScreen}
                      timesheetView={timesheetView}
                      timesheetType={timesheetType}
                      setTimesheetView={(view) => setTimesheetView(view)}
                      handleZoom={(type) => handleZoom(type)}
                      showSelectionAnimation={showSelectionAnimation}
                      setShowSelectionAnimation={setShowSelectionAnimation}
                      predictioncount={predictioncount}
                    />
                  ) : (
                    <ActivityList
                      zoomLevel={zoomLevel}
                      filter={filter}
                      generateSummary={generateSummary}
                      setIsFilterDone={setIsFilterDone}
                    />
                  )}
                </div>
              </div>
              <div
                className={css`
                  height: 100%;
                  width: ${100 - firstInnerWidth}%;
                  padding: 0 14px 0px 14px;
                  position: relative;
                  background-color: var(--background-color-11);
                  margin-left: 5px;
                  overflow: scroll;
                  z-index: 1;
                  ::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: transparent;
                    z-index: ${isAILoading ? 9 : -1};
                  }
                `}
              >
                <div
                  className={css`
                    cursor: ew-resize;
                    height: 100%;
                    min-width: 20px;
                    position: absolute;
                    left: 0;
                  `}
                  onMouseDown={handleMouseDown}
                ></div>
                {eventsToLog?.length < 1 && timesheetEvents.length < 1 && (
                  <Worklog
                    id="btn-add-nw-evnt"
                    filter={filter}
                    setIsLogged={setIsLogged}
                    isLogged={isLogged}
                    generateSummary={generateSummary}
                    setGenerateSummary={(state) => setGenerateSummary(state)}
                    predictioncount={predictioncount}
                  />
                )}
                {eventsToLog?.length > 0 &&
                  (!hasTimesheetFeature) && (
                    <LogWork
                      filter={filter}
                      eventsToLog={eventsToLog}
                      setIsLogged={setIsLogged}
                    />
                  )}
                {timesheetEvents?.length > 0 && (
                  <EditWorkLog
                    eventsToLog={timesheetEvents}
                    timesheetToEdit={timesheetToEdit}
                  />
                )}
                {eventsToLog?.length > 0 && hasTimesheetFeature && (
                  <AITimesheetPredictions
                    filter={filter}
                    eventsToLog={eventsToLog}
                    hasTimesheetFeature={hasTimesheetFeature}
                    setHasTimesheetFeature={setHasTimesheetFeature}
                  />
                )}
              </div>
            </div>
          )}
        </ContentLayout>

        <TimesheetProjectReport
          id="btn-timesheeet"
          open={projectReportOpen}
          onClose={() => {
            setProjectReportOpen(false);
            setTextSummary(undefined);
            setIsLoading(false);
          }}
          startDate={startDate}
          endDate={endDate}
          userId={memberFilter}
        />
        {Number(predictioncount.count) === 0 ? (
          <TimesheetPopup
            setPredictionCount={setPredictionCount}
            setShowSelectionAnimation={setShowSelectionAnimation}
          />
        ) : Number(predictioncount.count) < 10 ? (
          <LavaroPopup
            setPredictionCount={setPredictionCount}
            activeStarIndex={predictioncount.count}
            setShowSelectionAnimation={setShowSelectionAnimation}
          />
        ) : (
          showBootcamp && (
            <Bootcamp
              setPredictionCount={(e) => {
                setPredictionCount(e);
                CommonUtil.setCookie("complition_prediction", true, 1);
              }}
              setShowBootcamp={setShowBootcamp}
              activeStarIndex={predictioncount.count}
              setShowSelectionAnimation={setShowSelectionAnimation}
            />
          )
        )}
      </TimesheetContext.Provider>
    </>
  );
}
